// Framework-agnostic pub-sub store.
//
// Why this exists (design.md ADR-5):
//   - `lib/ws.ts` must NOT import `svelte/store`. It is a plain singleton
//     that pushes snapshot frames into a publisher; consumers subscribe
//     however they like.
//   - Svelte 4's `$store` syntax accepts any object exposing
//     `subscribe(fn): () => void`, so components can keep using `$snapshot`
//     without migration cost.
//
// Contract:
//   - `subscribe(fn)` → unsubscribe; calls `fn(value)` synchronously on
//     registration with the current value.
//   - `set(value)` replaces the value. If the new value is `===` the
//     previous one, no notification fires (cheap dedupe).
//   - `update(fn)` reads, mutates, sets.
//   - `derived(fn)` returns a read-only store that re-emits whenever the
//     source changes.
//   - `getValue(store)` returns the current snapshot synchronously — a
//     drop-in for `get(...)` from `svelte/store`.
//
// Type-safety: `createStore<T>(initial)` exposes `T` to subscribers; the
// derived store is typed as the result of the projection function.

export interface ReadableStore<T> {
  subscribe(listener: (value: T) => void): () => void;
}

export interface WritableStore<T> extends ReadableStore<T> {
  set(value: T): void;
  update(mutator: (prev: T) => T): void;
}

export interface DerivedStore<T> extends ReadableStore<T> {
  // Hook kept for symmetry with ReadableStore<T> — derived stores are
  // refreshed automatically by their source listener, so this is a
  // no-op in practice. Tests/extensions may use it to force a refresh
  // when the projection closes over external state.
  _refresh(): void;
}

export interface StoreWithDerived<T> extends WritableStore<T> {
  derived<U>(project: (value: T) => U): DerivedStore<U>;
}

/**
 * Synchronously read the current value of a store. Mirrors
 * `get(store)` from `svelte/store` so legacy call-sites stay one-line.
 */
export function getValue<T>(store: ReadableStore<T>): T {
  let value!: T;
  const unsub = store.subscribe((v) => {
    value = v;
  });
  unsub();
  return value;
}

/**
 * Build a tiny namespaced localStorage adapter that survives
 * SSR/test environments where `localStorage` is missing.
 */
function safeLocalStorage(): {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
} | null {
  if (typeof localStorage === "undefined") return null;
  return {
    getItem: (k) => {
      try {
        return localStorage.getItem(k);
      } catch {
        return null;
      }
    },
    setItem: (k, v) => {
      try {
        localStorage.setItem(k, v);
      } catch {
        /* quota / private mode — carry on */
      }
    },
    removeItem: (k) => {
      try {
        localStorage.removeItem(k);
      } catch {
        /* ignore */
      }
    },
  };
}

export function createStore<T>(initial: T): StoreWithDerived<T> {
  let current: T = initial;
  const listeners: Set<(value: T) => void> = new Set();

  function notify(value: T): void {
    // Snapshot the listener set so an unsubscribe during delivery
    // cannot break iteration (the Svelte store contract allows this).
    for (const l of [...listeners]) l(value);
  }

  const base: WritableStore<T> = {
    subscribe(listener) {
      listeners.add(listener);
      listener(current);
      return () => {
        listeners.delete(listener);
      };
    },
    set(value) {
      if (value === current) return;
      current = value;
      notify(current);
    },
    update(mutator) {
      const next = mutator(current);
      base.set(next);
    },
  };

  function derived<U>(project: (value: T) => U): DerivedStore<U> {
    let projected: U = project(current);
    const dListeners: Set<(value: U) => void> = new Set();

    function notifyDerived(value: U): void {
      for (const l of [...dListeners]) l(value);
    }

    // Re-emit whenever the source changes. We attach via the base
    // store's subscribe and never expose the unsubscribe handle —
    // a derived store's lifecycle is tied to its source.
    base.subscribe((next) => {
      const proj = project(next);
      if (proj === projected) return;
      projected = proj;
      notifyDerived(projected);
    });

    return {
      subscribe(listener) {
        dListeners.add(listener);
        listener(projected);
        return () => {
          dListeners.delete(listener);
        };
      },
      _refresh() {
        const next = project(current);
        if (next === projected) return;
        projected = next;
        notifyDerived(projected);
      },
    };
  }

  return { ...base, derived };
}

// --- persisted helpers --------------------------------------------------

/**
 * Read a JSON value from localStorage; returns the supplied fallback
 * when the entry is missing or malformed.
 */
export function readPersisted<T>(key: string, fallback: T): T {
  const store = safeLocalStorage();
  if (store === null) return fallback;
  const raw = store.getItem(key);
  if (raw === null) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

/**
 * Persist a JSON value to localStorage. `null` removes the key.
 * Silent no-op when localStorage is unavailable.
 */
export function writePersisted<T>(key: string, value: T | null): void {
  const store = safeLocalStorage();
  if (store === null) return;
  if (value === null) {
    store.removeItem(key);
  } else {
    store.setItem(key, JSON.stringify(value));
  }
}
